/**
 * Custom fetch utility for making API requests
 * Handles authentication and common error cases
 */

// Make sure the base URL includes the API version path
const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.joinprelude.co/api/v1';

// Base fetch function
const fetchWithAuth = async (url, options = {}) => {
  console.log('🚀 CustomFetch Request:', {
    url,
    method: options.method || 'GET',
    headers: options.headers,
    body: options.body ? JSON.parse(options.body) : undefined
  });

  // Get the token from localStorage
  const token = localStorage.getItem('token');
  console.log('📝 Token from localStorage:', token ? 'Present' : 'Not found');

  // Default options
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  };

  // Merge options
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  console.log('🔧 Merged Request Options:', {
    ...mergedOptions,
    headers: mergedOptions.headers,
    body: mergedOptions.body ? JSON.parse(mergedOptions.body) : undefined
  });

  try {
    // Prepend the base URL if the URL doesn't already include http:// or https://
    // Ensure proper path joining (avoid double slashes)
    let fullUrl;
    if (url.startsWith('http')) {
      fullUrl = url;
    } else {
      // Remove leading slash from the URL if BASE_URL ends with a slash
      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const apiPath = url.startsWith('/') ? url : `/${url}`;
      fullUrl = `${baseUrl}${apiPath}`;
    }
    console.log('📡 Sending request to:', fullUrl);
    const response = await fetch(fullUrl, mergedOptions);
    console.log('📥 Response status:', response.status);
    console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));

    // Handle unauthorized access
    if (response.status === 401) {
      console.error('🚫 Unauthorized access - clearing token and redirecting to login');
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Unauthorized access');
    }

    // Handle other error responses
    if (!response.ok) {
      const error = await response.json();
      console.error('❌ Request failed:', error);
      throw new Error(error.message || 'An error occurred');
    }

    const data = await response.json();
    console.log('✅ Response data:', data);
    return data;
  } catch (error) {
    console.error('❌ Request error:', {
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// Create a customFetch object with HTTP method functions
export const customFetch = {
  // GET request
  get: (url, options = {}) => fetchWithAuth(url, { ...options, method: 'GET' }),
  
  // POST request
  post: (url, data, options = {}) => fetchWithAuth(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  // PUT request
  put: (url, data, options = {}) => fetchWithAuth(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  // PATCH request
  patch: (url, data, options = {}) => fetchWithAuth(url, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data)
  }),
  
  // DELETE request
  delete: (url, options = {}) => fetchWithAuth(url, {
    ...options,
    method: 'DELETE'
  })
};

// Also export the base fetch function for direct use if needed
export { fetchWithAuth };
